import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/Login/AuthContext';

/**
 * useSmartBack — reliable "back" navigation for on-screen back buttons.
 *
 * iOS has no system back button, so every non-root screen must offer its own
 * on-screen control (App Store Guideline 4). `window.history.back()` /
 * `navigate(-1)` are silent no-ops when the screen is the first history entry
 * (deep link, hard refresh, or PWA/app cold launch), which strands the user.
 *
 * This hook goes back when there is real history to go back to, and otherwise
 * falls back to a safe destination:
 *   - a caller-supplied `fallback`, if provided; otherwise
 *   - `/dashboard` when the user is logged in, or `/home` when they are not
 *     (so public/legal pages opened directly still land somewhere sensible).
 *
 * @param {string} [fallback] optional explicit fallback route
 * @returns {() => void} click handler for the back control
 */
export default function useSmartBack(fallback) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return useCallback(() => {
    if (typeof window !== 'undefined' && window.history.state && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(fallback || (isAuthenticated ? '/dashboard' : '/home'));
  }, [navigate, isAuthenticated, fallback]);
}
